import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  memo,
} from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { Menu } from '@headlessui/react';

import { Icon, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';
import { Button } from 'components/form';
import ProfilePics from 'components/candidate/ProfilePics';
import TouchpointCard from 'components/candidate/TouchpointCard';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useCompanyProfile } from 'hooks/UseCompanyProfile.hook';
import { useAuth } from 'lib/providers/AuthProvider';
import { PAGE_ROUTES } from 'lib/page-routes';

import { useJobs } from 'lib/contexts/hiring-context';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { TouchpointType } from 'lib/models/touchpoint';
import { CompanyAttributes, CompanyResponse } from 'lib/models/company';
import { JobTouchpointable } from 'lib/models/job';
import { formatToK } from 'utils/format';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import JobsTable from '../JobsTable';
import styles from './Hiring.module.scss';

export const Hiring: React.FC = memo(() => {
  const router = useRouter();
  const { isRecruiter } = useAuth();

  const {
    company,
    companyDashboard,
    mutateCompanyDashboard,
    similarOpportunities,
  } = useCompanyProfile();

  const scrollTabRef = useRef<HTMLDivElement | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  const { setSelectedState } = useJobs();

  useEffect(() => {
    if (!isRecruiter) {
      router.replace(PAGE_ROUTES.SIGN_IN_RECRUITER);
    }
  }, [isRecruiter]);

  const {
    live_non_event_touchpoint_applications = 0,
    total_count_map,
    live_count_map,
    closed_count_map,
    live_non_event_not_reviewed_touchpoint_applications = 0,
    live_non_reviewed_not_event_avatar_url,
    not_reviewed_not_events_touchpoints,
  } = companyDashboard || {};

  const { Job: live_job_count = 0, Internship: live_internship_count = 0 } =
    live_count_map || {};

  const { Job: total_job_count = 0, Internship: total_internship_count = 0 } =
    total_count_map || {};

  const { Job: closed_job_count = 0, Internship: closed_internship_count = 0 } =
    closed_count_map || {};

  const reviews_avatar_urls = useMemo(() => {
    return live_non_reviewed_not_event_avatar_url
      ?.filter((item) => item.avatar_url !== null)
      ?.map((item) => item.avatar_url);
  }, [live_non_reviewed_not_event_avatar_url]);

  const reviewOpportunityList = useMemo(() => {
    return not_reviewed_not_events_touchpoints?.map(
      ({ id, title, not_reviewed_application_count }) => {
        return (
          <button
            key={id}
            onClick={() => {
              router.push(`${PAGE_ROUTES.HIRING}/${id}`);
            }}
          >
            <span>{title} </span>
            <span>({not_reviewed_application_count})</span>
          </button>
        );
      }
    );
  }, [not_reviewed_not_events_touchpoints]);

  const getReviewCTA = useCallback(
    (buttonLabel: string, startIcon: boolean, color: 'primary' | 'white') => {
      if (!not_reviewed_not_events_touchpoints) return;

      if (not_reviewed_not_events_touchpoints?.length === 0)
        return (
          <ButtonV3
            isFullWidth
            onClick={() => {
              scrollTabRef.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }}
          >
            View jobs
          </ButtonV3>
        );

      if (not_reviewed_not_events_touchpoints?.length === 1)
        return (
          <ButtonV3
            isFullWidth
            onClick={() => {
              if (
                not_reviewed_not_events_touchpoints &&
                not_reviewed_not_events_touchpoints.length === 1
              ) {
                const id = not_reviewed_not_events_touchpoints[0].id;
                router.push(`${PAGE_ROUTES.HIRING}/${id}`);
              } else return;
            }}
            className={styles.reviewMenu}
          >
            {buttonLabel}
          </ButtonV3>
        );

      return (
        <Menu as="div" className={styles.reviewMenu}>
          <Menu.Button
            className={cn(
              styles.trigger,
              { [styles.triggerPrimary]: color === 'primary' },
              { [styles.triggerWhite]: color === 'white' }
            )}
          >
            <Row align="center" justify="center" columnGap={8}>
              {startIcon ? (
                <Icon iconName="icon_check-list" size="small" />
              ) : null}
              {buttonLabel}
              <Icon iconName="icon_arrow-down" size="xsmall" />
            </Row>
          </Menu.Button>
          <Menu.Items as="ul" className={styles.touchpoint}>
            <Menu.Item as="li" className={styles.item}>
              {reviewOpportunityList}
            </Menu.Item>
          </Menu.Items>
        </Menu>
      );
    },
    [not_reviewed_not_events_touchpoints]
  );

  const onHeaderShow = useCallback(() => {
    setHeaderScrolled(false);
  }, []);

  const onHeaderHide = useCallback(() => {
    setHeaderScrolled(true);
  }, []);

  useVisibleOnIntersect({
    elRef: headerRef,
    threshold: 1,
    onIntersect: onHeaderShow,
    onHidden: onHeaderHide,
    rootMargin: '0px',
    isEqualToThreshold: true,
  });

  const similarOpportunityList = useMemo(() => {
    return similarOpportunities?.map(
      ({
        id,
        title,
        start_date,
        creatable_for,
        posted_at,
        created_at,
        touchpointable_type,
        touchpointable: touchpointableResponse,
        pay_currency,
        application_deadline,
        kind,
        slug,
        cohost_applications: cohostApplicationsResponse,
        is_eligible_for_auto_apply,
      }) => {
        const createdBy = parseResponse<CompanyAttributes>(
          creatable_for as CompanyResponse
        );
        const { name, icon_url, is_verified, huzzle_verified } = createdBy;

        const touchpointable = parseResponse(
          touchpointableResponse as JobTouchpointable
        );
        const { minimum_salary, maximum_salary } = touchpointable;

        const publicPageUrl = `${
          window.location.origin
        }${getOpportunityPublicPagePathname(touchpointable_type, slug)}`;

        const cohost_applications = parseArrayResponse(
          cohostApplicationsResponse
        );
        return (
          <div key={id} className={styles.similarlistCard}>
            <TouchpointCard
              icon_url={icon_url}
              title={title}
              createdBy={name}
              startDate={start_date}
              is_eligible_for_auto_apply={is_eligible_for_auto_apply}
              created_at={posted_at ? posted_at : created_at}
              application_deadline={application_deadline}
              is_verified={is_verified}
              touchpointable_type={touchpointable_type as TouchpointType}
              maximum_salary={maximum_salary}
              minimum_salary={minimum_salary}
              pay_currency={pay_currency || 'GBP'}
              url={publicPageUrl}
              isExpired={false}
              className={styles.match}
              kind={kind}
              isOnboarding={true}
              onNewTab={true}
              chipClassName={styles.chip}
              id={id}
              slug={slug}
              huzzle_verified={huzzle_verified}
              isNewSet={false}
              cities={null}
              showManagerProgress={false}
              cohost_applications={cohost_applications}
              following={null}
              apply_url={null}
              extension_installed={true}
            />
          </div>
        );
      }
    );
  }, [similarOpportunities]);

  useEffect(() => {
    return () => {
      scrollTabRef.current = null;
    };
  }, []);

  return (
    <section className={styles.Hiring} data-testid="Hiring">
      {headerScrolled ? null : (
        <Row
          align="center"
          columnGap={4}
          className={styles.redirect}
          onClick={() => router.push(PAGE_ROUTES.RECRUITER_DASHBOARD)}
        >
          <Icon iconName="chevron-left" size="large" />
          Dashboard
        </Row>
      )}
      <div
        ref={headerRef}
        className={cn({
          [styles.header]: true,
          [styles.headerScrolled]: headerScrolled,
        })}
      >
        <p className={styles.title}>Hiring</p>
        <ButtonV3
          color={
            total_job_count === 0 && total_internship_count === 0
              ? 'secondary'
              : 'primary'
          }
          onClick={() => router.push(`${PAGE_ROUTES.HIRING_CREATE}?step=1`)}
          className={styles.headerButton}
        >
          Create new
        </ButtonV3>
      </div>
      {headerScrolled || !companyDashboard ? null : (
        <Row align="center" columnGap={4} className={styles.totalcount}>
          <p>{total_job_count} Jobs</p>•
          <p>{total_internship_count} Internships</p>
        </Row>
      )}
      {!companyDashboard ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.container}>
          {total_job_count === 0 && total_internship_count === 0 ? (
            <div className={styles.noResults}>
              <div className={styles.noResultsContent}>
                <Icon iconName="desert" size="auto" />
                <div className={styles.noResultsContentText}>
                  <p>Post your first job or internship</p>
                  <span>
                    Post your first job or internship and get thousands of
                    potential students matched to it - takes only a few mins!
                  </span>
                </div>
                <ButtonV3
                  onClick={() =>
                    router.push(`${PAGE_ROUTES.HIRING_CREATE}?step=1`)
                  }
                  className={styles.noResultsContentButton}
                >
                  Start Hiring
                </ButtonV3>
              </div>
              <div className={styles.separator}></div>
              <p className={styles.similarlistTitle}>
                Here are some live opportunities from other companies, check
                them out to get an idea of how it looks!
              </p>
              <div className={styles.similarlist}>{similarOpportunityList}</div>
              <ButtonV3
                onClick={() =>
                  router.push(`${PAGE_ROUTES.HIRING_CREATE}?step=1`)
                }
                isFullWidth
                className={styles.similarlistButton}
              >
                Create new
              </ButtonV3>
            </div>
          ) : (
            <>
              <section className={styles.island1}>
                <div
                  className={cn(styles.island1Div, {
                    [styles.island1DivActive]:
                      live_non_event_touchpoint_applications > 0,
                  })}
                >
                  <div>
                    <p className={styles.island1Title}>Applications</p>
                    <Row align="center" columnGap={8}>
                      <p className={styles.island1Count}>
                        {live_non_event_touchpoint_applications}
                      </p>
                      {live_non_event_not_reviewed_touchpoint_applications >
                      0 ? (
                        <p className={styles.new}>
                          {live_non_event_not_reviewed_touchpoint_applications}{' '}
                          new
                        </p>
                      ) : null}
                    </Row>
                  </div>
                  <div>
                    {live_non_event_not_reviewed_touchpoint_applications ? (
                      <Row columnGap={12} className={cn(styles.subscriberRow)}>
                        <ProfilePics
                          maxPicsToShow={3}
                          urls={reviews_avatar_urls || []}
                        />
                        <p className={styles.subscriberText}>
                          {formatToK(
                            live_non_event_not_reviewed_touchpoint_applications
                          )}{' '}
                          candidates left for review.
                        </p>
                      </Row>
                    ) : null}
                    {getReviewCTA('Review', true, 'primary')}
                  </div>
                </div>
                <div className={styles.island1Div}>
                  <div>
                    <p className={styles.island1Title}>
                      Live jobs & internships
                    </p>
                    <p className={styles.island1Count}>
                      {Number(live_job_count) + Number(live_internship_count)}
                    </p>
                  </div>
                  {live_job_count === 0 && live_internship_count === 0 ? (
                    <Button
                      color="white"
                      onClick={() =>
                        router.push(`${PAGE_ROUTES.HIRING_CREATE}?step=1`)
                      }
                      isFullWidth
                      className={styles.whitebutton}
                    >
                      Add new
                    </Button>
                  ) : (
                    <Row columnGap={10} isFullWidthRow>
                      <Button
                        color="white"
                        className={styles.whitebutton}
                        onClick={() => {
                          setSelectedState(1);
                          scrollTabRef.current?.scrollIntoView({
                            behavior: 'smooth',
                          });
                        }}
                      >
                        View
                      </Button>
                      <Button
                        color="white"
                        className={styles.whitebutton}
                        onClick={() =>
                          router.push(`${PAGE_ROUTES.HIRING_CREATE}?step=1`)
                        }
                      >
                        Add new
                      </Button>
                    </Row>
                  )}
                </div>
                <div className={styles.island1Div}>
                  <div>
                    <p className={styles.island1Title}>
                      Closed jobs & internships
                    </p>
                    <p className={styles.island1Count}>
                      {Number(closed_job_count) +
                        Number(closed_internship_count)}
                    </p>
                  </div>
                  {closed_job_count || closed_internship_count ? (
                    <Button
                      color="white"
                      className={styles.whitebutton}
                      onClick={() => {
                        setSelectedState(4);
                        scrollTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }}
                    >
                      View
                    </Button>
                  ) : null}
                </div>
              </section>

              <div ref={scrollTabRef}>
                {company && (
                  <JobsTable
                    companyDashboard={companyDashboard}
                    mutateCompanyDashboard={mutateCompanyDashboard}
                    companyId={company.id}
                    companySlug={company.slug}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
});

Hiring.displayName = 'Hiring';
